import React from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import Chip from "@mui/material/Chip";
import CircularProgress from "@mui/material/CircularProgress";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableRow from "@mui/material/TableRow";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import {
  confirmAssistantMutation,
  cancelAssistantMutation,
} from "../api/client";
import { navigateAssistantHref, extractAssistantDeepLink } from "../utils/assistantNavigation";

/**
 * Card de proposta do assistente: nada é gravado até Confirmar.
 */
export default function AssistantConfirmationCard({ artifact, onNavigate }) {
  const { t } = useTranslation("shell");
  const navigate = useNavigate();
  const location = useLocation();
  const [status, setStatus] = React.useState("pending"); // pending|confirming|cancelling|confirmed|cancelled|error
  const [error, setError] = React.useState("");
  const [result, setResult] = React.useState(null);

  if (!artifact?.proposalUuid) return null;

  const rows = Array.isArray(artifact?.preview?.rows) ? artifact.preview.rows : [];
  const expiresAt = artifact.expiresAt ? new Date(artifact.expiresAt) : null;
  const expired = expiresAt && !Number.isNaN(expiresAt.getTime()) && expiresAt.getTime() < Date.now();

  const handleConfirm = async () => {
    if ((status !== "pending" && status !== "error") || expired) return;
    setStatus("confirming");
    setError("");
    try {
      const res = await confirmAssistantMutation(artifact.proposalUuid);
      setResult(res);
      setStatus("confirmed");
    } catch (e) {
      setError(
        (typeof e?.message === "string" && e.message) ||
          t("platformAi.confirmError", { defaultValue: "Não foi possível confirmar." })
      );
      setStatus("error");
    }
  };

  const handleCancel = async () => {
    if (status !== "pending" && status !== "error") return;
    setStatus("cancelling");
    setError("");
    try {
      await cancelAssistantMutation(artifact.proposalUuid);
      setStatus("cancelled");
    } catch (e) {
      setError(
        (typeof e?.message === "string" && e.message) ||
          t("platformAi.cancelError", { defaultValue: "Não foi possível cancelar." })
      );
      setStatus("error");
    }
  };

  const deepLink = extractAssistantDeepLink(result);

  const openLink = (e) => {
    e?.preventDefault?.();
    e?.stopPropagation?.();
    if (!deepLink) return;
    navigateAssistantHref(deepLink, {
      pathname: location.pathname,
      navigate,
      onBeforeNavigate: () => onNavigate?.(),
    });
  };

  return (
    <Box
      sx={{
        mt: 0.75,
        p: 1.25,
        border: "1px solid",
        borderColor: status === "confirmed" ? "success.light" : "divider",
        borderRadius: 1,
        bgcolor: "background.paper",
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.75, flexWrap: "wrap" }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
          {artifact.title ||
            t("platformAi.confirmTitleFallback", { defaultValue: "Confira antes de continuar" })}
        </Typography>
        <Chip
          size="small"
          label={
            artifact.permissionChip ||
            t("platformAi.confirmAllowed", { defaultValue: "Você pode fazer isso" })
          }
          color="primary"
          variant="outlined"
        />
      </Box>

      {artifact.summary ? (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          {artifact.summary}
        </Typography>
      ) : null}

      <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1 }}>
        {t("platformAi.confirmHint", {
          defaultValue: "Confira os dados. Nada é enviado ou salvo até você confirmar.",
        })}
      </Typography>

      {rows.length > 0 && (
        <Table size="small" sx={{ mb: 1 }}>
          <TableBody>
            {rows.map((row) => (
              <TableRow key={row.key}>
                <TableCell sx={{ py: 0.5, pl: 0, width: "36%", color: "text.secondary" }}>
                  {row.label || row.key}
                </TableCell>
                <TableCell sx={{ py: 0.5, pr: 0 }}>{row.value}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}

      {expiresAt && status === "pending" && (
        <Typography variant="caption" color={expired ? "error" : "text.secondary"} sx={{ display: "block", mb: 1 }}>
          {expired
            ? t("platformAi.confirmExpired", {
                defaultValue: "Esta proposta expirou. Peça de novo para gerar outra.",
              })
            : t("platformAi.confirmExpires", {
                defaultValue: "Expira em {{time}}",
                time: expiresAt.toLocaleTimeString(),
              })}
        </Typography>
      )}

      {status === "confirmed" && (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1, color: "success.main" }}>
          <CheckCircleOutlineIcon fontSize="small" />
          <Typography variant="body2">
            {t("platformAi.confirmSuccess", { defaultValue: "Pronto! Tudo certo." })}
          </Typography>
        </Box>
      )}

      {status === "cancelled" && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            {t("platformAi.confirmCancelled", {
              defaultValue: "Cancelado. Nada foi alterado.",
            })}
        </Typography>
      )}

      {(status === "error" || error) && (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1, color: "error.main" }}>
          <ErrorOutlineIcon fontSize="small" />
          <Typography variant="body2">{error}</Typography>
        </Box>
      )}

      <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
        {(status === "pending" || status === "error") && !expired && (
          <>
            <Button
              size="small"
              variant="contained"
              onClick={handleConfirm}
              disabled={status === "confirming" || status === "cancelling"}
            >
              {t("platformAi.confirmAction", { defaultValue: "Confirmar" })}
            </Button>
            <Button size="small" variant="outlined" onClick={handleCancel}>
              {t("platformAi.cancelAction", { defaultValue: "Cancelar" })}
            </Button>
          </>
        )}
        {(status === "confirming" || status === "cancelling") && (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <CircularProgress size={16} />
            <Typography variant="caption">
              {status === "confirming"
                ? t("platformAi.confirming", { defaultValue: "Enviando…" })
                : t("platformAi.cancelling", { defaultValue: "Cancelando…" })}
            </Typography>
          </Box>
        )}
        {status === "confirmed" && deepLink && (
          <Button size="small" variant="outlined" onClick={openLink}>
            {t("platformAi.openRecord", { defaultValue: "Abrir na tela" })}
          </Button>
        )}
      </Box>
    </Box>
  );
}
